import React, { useEffect, useState, useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { subMonths, startOfMinute, endOfMinute } from 'date-fns';
import dateFormat from 'dateformat';
import DateRange from '#component/general/date-range';
import { getRelatedTo } from '#component/general/list-filter/helper';
import MAX_LOG_OBSERVATION_TIME_PERIOD from '#type/log-constants';
import { getSessionDateRange, setSessionDateRange } from '#component/general/date-range/helper';
import { DateRangeWrapper } from '#component/general/list-filter/components/general/styles';
import { getDateRange, isInMonthRange } from '#helper';

const getMinDate = () => new Date(startOfMinute(subMonths(new Date(), 3)));

export const DatePicker = ({ handleChange, handleFetchFilterData }) => {
  const defaultEnd = new Date();
  const defaultStart = getMinDate(new Date(defaultEnd));
  const [isThreeMonthRange, setThreeMonthRange] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [openEndDateDialog, setOpenEndDateDialog] = useState(false);
  const relatedTo = getRelatedTo();
  const filter = useSelector(s => s.listFilter?.[relatedTo], shallowEqual) ?? {};
  const start = filter?.start?.value;
  const end = filter?.end?.value;

  // Get stored values from sessionStorage (common for all log pages)
  const storedDateRange = getSessionDateRange();
  const storedStart = storedDateRange?.start;
  const storedEnd = storedDateRange?.end;

  useEffect(() => {
    if (!initialized) {
      if (storedStart && storedEnd && !start && !end) {
        // Apply stored values to filter
        handleChange(storedStart, 'start', relatedTo);
        handleChange(storedEnd, 'end', relatedTo);
      }
      setInitialized(true);
    }
  }, [initialized, start, end, storedStart, storedEnd, relatedTo, handleChange]);

  useEffect(() => {
    if (start && end) {
      const range = getDateRange(start, end);
      const isInThreeMonthRange = isInMonthRange(range.start, range.end, 3);
      setThreeMonthRange(isInThreeMonthRange);
    }
  }, [start, end]);

  const changeDateRange = useCallback((startDate, endDate) => {
    const range = {
      start: dateFormat(startOfMinute(new Date(startDate)), 'isoUtcDateTime'),
      end: dateFormat(endOfMinute(new Date(endDate)), 'isoUtcDateTime')
    };

    // Update Redux state
    Object.keys(range).forEach(key => handleChange(range[key], key, relatedTo));

    // Save to sessionStorage immediately to ensure persistence (common for all log pages)
    setSessionDateRange(range.start, range.end);

    const isInThreeMonthRange = isInMonthRange(startDate, endDate, MAX_LOG_OBSERVATION_TIME_PERIOD);
    setThreeMonthRange(isInThreeMonthRange);

    if (isInThreeMonthRange) {
      handleFetchFilterData();
    }
  }, [handleChange, handleFetchFilterData, relatedTo]);

  return (
    <DateRangeWrapper>
      <DateRange
        rangeStart={start || storedStart || defaultStart}
        rangeEnd={end || storedEnd || defaultEnd}
        maxDate={dateFormat(endOfMinute(new Date()), 'isoUtcDateTime')}
        setDateRange={changeDateRange}
        isInRange={isThreeMonthRange}
        totalMonthRange={MAX_LOG_OBSERVATION_TIME_PERIOD}
        openEndDateDialog={openEndDateDialog}
        setOpenEndDateDialog={setOpenEndDateDialog}
      />
    </DateRangeWrapper>
  );
};
